/**
 * SCRUM-368 (DS-026 S6) plan P6 — VRBO SSO-onboarding + confirm-mapping Zod
 * schemas.
 *
 * Endpoints covered (`ChannelPropertyMappingController`):
 *   POST .../vrbo/sso-redirect     — InitiateVrboSsoOnboardingUseCase
 *   POST .../vrbo/confirm-mapping  — ConfirmVrboMappingUseCase
 *
 * H2 (DS-026 §5): `ConfirmVrboMappingRequestSchema` is DELIBERATELY MISSING
 * an `advertiserId` field — the archive-hazardous `confirmMapping` egress
 * acts on ALL VRBO mappings under an advertiser, so the controller resolves
 * `advertiserId` SERVER-SIDE from the current property's own VRBO mapping's
 * `channelAdvertiserId`, never trusting the caller to name which advertiser
 * gets confirmed. `acknowledgeArchiveHazard` is the ONLY body field.
 *
 * `InitiateVrboSsoOnboardingRequestSchema`'s wire field is named
 * `advertiserId` (an OPTIONAL per-property Flow-2 override, `[F11]`) —
 * mapped at the controller boundary to the application command's
 * `propertyAdvertiserId` field. This is a DIFFERENT concept from
 * `ConfirmVrboMappingRequestSchema`'s (absent) advertiser scoping.
 *
 * `companyName`/`advertiserId`: opaque-identifier-shaped strings, mirroring
 * `cancellationPolicyRef` / `ChannelPmSettingsSchema`'s per-field rules
 * (trim/min/max/no-trailing-separator/no-wildcard-only/no-injection-fragment)
 * at the 128-char OPAQUE_IDENTIFIER_MAX_LENGTH ceiling. `email` uses Zod's
 * RFC email-shape check (the authoritative structural gate) plus the same
 * injection-fragment defense-in-depth.
 */
import { z } from 'zod';
/** Must match OPAQUE_IDENTIFIER_MAX_LENGTH (opaque-identifier.ts). */
const OPAQUE_ID_MAX = 128;
/** Generous email ceiling — distinct from the RFC email-shape check itself. */
const EMAIL_MAX = 255;
function opaqueIdentifierSchema(label) {
    return z
        .string()
        .trim()
        .min(1)
        .max(OPAQUE_ID_MAX)
        .refine((v) => !v.endsWith('/') && !v.endsWith('.') && !v.endsWith('-'), {
        message: `${label} must not end with a separator`,
    })
        .refine((v) => !/^\*+$/.test(v), { message: `${label} must not be a wildcard-only value` })
        .refine((v) => !['<', '>', '${', ';', '..'].some((fragment) => v.includes(fragment)), {
        message: `${label} must not contain unsafe characters`,
    });
}
// ---------------------------------------------------------------------------
// POST body — InitiateVrboSsoOnboardingRequest
// ---------------------------------------------------------------------------
export const InitiateVrboSsoOnboardingRequestSchema = z
    .object({
    advertiserId: opaqueIdentifierSchema('advertiserId').optional(),
    companyName: opaqueIdentifierSchema('companyName'),
    email: z
        .string()
        .trim()
        .min(1)
        .max(EMAIL_MAX)
        .email()
        .refine((v) => !['<', '>', '${', ';'].some((fragment) => v.includes(fragment)), {
        message: 'email must not contain unsafe characters',
    }),
})
    .strict();
// ---------------------------------------------------------------------------
// Response — SsoRedirectLink / InitiateVrboSsoOnboardingResponse
// ---------------------------------------------------------------------------
export const SsoRedirectLinkSchema = z.object({
    region: z.string(),
    url: z.string().url(),
});
export const InitiateVrboSsoOnboardingResponseSchema = z.object({
    propertyChannelMappingId: z.string(),
    enablementState: z.string(),
    links: z.array(SsoRedirectLinkSchema),
});
// ---------------------------------------------------------------------------
// POST body — ConfirmVrboMappingRequest (H2: NO advertiserId field)
// ---------------------------------------------------------------------------
export const ConfirmVrboMappingRequestSchema = z
    .object({
    acknowledgeArchiveHazard: z.boolean(),
})
    .strict();
// ---------------------------------------------------------------------------
// Response — ConfirmVrboMappingResponse
// ---------------------------------------------------------------------------
export const ConfirmVrboMappingResponseSchema = z.object({
    advertiserId: z.string(),
    confirmedMappingIds: z.array(z.string()),
    takeoverConfirmedAt: z.string().datetime(),
});
//# sourceMappingURL=vrbo-onboarding.js.map